/**
 * useReflectionReminder — best-effort, browser-only daily nudge.
 *
 * Reads `reflection_reminder_enabled` and `reflection_reminder_hour` from
 * localStorage and schedules a single setTimeout to the next occurrence of the
 * configured local hour. Fires a Notification (if permission is granted) when
 * the user hasn't already responded to today's prompt. Re-schedules daily.
 *
 * Intentionally lightweight — no service worker / push. Tab must be open.
 */
import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export const REMINDER_ENABLED_KEY = 'reflection_reminder_enabled';
export const REMINDER_HOUR_KEY = 'reflection_reminder_hour';
const DEFAULT_HOUR = 18;

function readPrefs() {
  if (typeof window === 'undefined') return { enabled: false, hour: DEFAULT_HOUR };
  const enabled = window.localStorage.getItem(REMINDER_ENABLED_KEY) === 'true';
  const rawHour = window.localStorage.getItem(REMINDER_HOUR_KEY);
  const hour = rawHour ? Math.max(0, Math.min(23, parseInt(rawHour, 10))) : DEFAULT_HOUR;
  return { enabled, hour: Number.isFinite(hour) ? hour : DEFAULT_HOUR };
}

function msUntilNext(hour: number): number {
  const now = new Date();
  const target = new Date(now);
  target.setHours(hour, 0, 0, 0);
  if (target.getTime() <= now.getTime()) {
    target.setDate(target.getDate() + 1);
  }
  return target.getTime() - now.getTime();
}

export function useReflectionReminder() {
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    if (typeof window === 'undefined' || typeof Notification === 'undefined') return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const schedule = () => {
      const { enabled, hour } = readPrefs();
      if (!enabled) return;
      if (Notification.permission !== 'granted') return;

      const ms = msUntilNext(hour);
      timer = setTimeout(async () => {
        if (cancelled) return;
        try {
          // Only nudge if the user hasn't responded to today's prompt
          const today = new Date().toISOString().slice(0, 10);
          const { data } = await (supabase as any)
            .from('daily_reflections')
            .select('id, responded_at, prompt_date')
            .eq('user_id', user.id)
            .eq('prompt_date', today)
            .maybeSingle();

          const alreadyAnswered = data && data.responded_at;
          if (!alreadyAnswered) {
            const n = new Notification("Today's reflection is ready", {
              body: 'One question, tuned to what your profile is uncertain about.',
              tag: 'psyforge-daily-reflection',
              icon: '/favicon.ico',
            });
            n.onclick = () => {
              window.focus();
              window.location.assign('/reflect');
              n.close();
            };
          }
        } catch {
          // best-effort — never throw from a timer
        }
        if (!cancelled) schedule();
      }, ms);
    };

    schedule();

    // React to setting changes from the same tab
    const onStorage = (e: StorageEvent) => {
      if (e.key === REMINDER_ENABLED_KEY || e.key === REMINDER_HOUR_KEY) {
        if (timer) clearTimeout(timer);
        schedule();
      }
    };
    window.addEventListener('storage', onStorage);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      window.removeEventListener('storage', onStorage);
    };
  }, [user]);
}